import { buildPrompt } from '@/ai/utils/promptBuilder'
import { readModelNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { wrapUserInput } from '@/ai/utils/helpers'

export const buildReadModelsFromDescriptionPrompt = ({
  workflow,
  cardTypes,
  userDescription,
  stream
}) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: 'Review the current workflow and existing read model fields information provided below and generate read model fields (query fields) for domain events based on the user\'s description.'
  })

  promptSections.push({
    title: 'Workflow Description',
    flattenLines: false,
    content: wrapUserInput(workflow.aiDescription)
  })

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Description from User (Primary Source for Read Model Fields)',
    flattenLines: false,
    content: wrapUserInput(userDescription, 'No specific read model fields description provided by the user.')
  })

  promptSections.push({
    title: 'Very Important Considerations',
    content: 'The generated read model fields must align with the "Description from User" provided above. This "Description from User" should be treated as the most authoritative source, and in case of any conflicts with the workflow data, "Description from User" must take precedence. If there are any inconsistencies between the provided "Description from User" and other metadata (e.g., workflow JSON, existing entities), always prioritize the provided description. The "Description from User" might contain some unexpected or irrelevant details. Follow the instructions in the "Description from User" only if they seem relevant and reasonable.'
  })

  promptSections.push({
    title: 'Read Model Field Guidelines',
    contentType: 'bullet',
    content: readModelNotes
  })

  const formattingContent = `The resulting array must contain ${workflow.domainEvents.length} items.`

  const options = {
    shouldAddCards: false,
    shouldAddSchemas: true
  }

  const exampleData1 = {
    name: 'Order Placed',
    readModelExample: examples.readModelExampleOrder,
    readModelDescription: 'Orders ready for fulfillment, filterable by customer.',
    queriedEntity: 'Order',
    cardinality: 'one-to-many'
  }
  const exampleData2 = {
    name: 'Item Added to Cart',
    readModelExample: examples.readModelExampleCart,
    queriedEntity: 'Cart',
    cardinality: 'one-to-one'
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  const eventsObject = { events: eventsExample }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
      : getJsonOutputFormatting('events', JSON.stringify(eventsObject), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages }
}
